import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message, Switch, DatePicker, ColorPicker, Row, Col, Typography, Checkbox } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAnnonceApi } from '../../api/annonce/annonces';
import { TAnnonce } from '../../models/annonce/annonce';
import AnnonceBanner from '../AdvertBanner/AnnonceBanner';

const { Title } = Typography;

interface Props {
    visible: boolean;
    onClose: () => void;
    annonce: TAnnonce | null;
    setAnnonces: React.Dispatch<React.SetStateAction<TAnnonce[]>>;
    isEditMode: boolean;
}

type AnnonceFormValues = Omit<TAnnonce, 'start_date' | 'end_date' | 'backgroundColor' | 'flag_tilmicode' | 'flag_tilmidplus'> & {
    start_date?: Dayjs;
    end_date?: Dayjs;
    backgroundColor?: string;
    flags?: ('tilmicode' | 'tilmidplus')[];
};

const palette = {
    primary: '#2992BF',
    secondary: '#8BBE1E',
    border: '#e0e7f1',
    textPrimary: '#0f172a',
    textMuted: '#6b7280',
    surface: '#ffffff',
};

const AnnonceBannerForm: React.FC<Props> = ({ visible, onClose, annonce, setAnnonces, isEditMode }) => {
    const { t } = useTranslation();
    const { createAnnonce, updateAnnonce } = useAnnonceApi();
    const [form] = Form.useForm<AnnonceFormValues>();

    const [previewAnnonce, setPreviewAnnonce] = useState<TAnnonce>({
        id: annonce?.id || 0,
        title: annonce?.title || '',
        description: annonce?.description || '',
        image_url: annonce?.image_url || '',
        background_color: annonce?.background_color || '#2992BF',
        start_date: annonce?.start_date || '',
        end_date: annonce?.end_date || '',
        actif: annonce?.actif || false,
        flag_tilmicode: annonce?.flag_tilmicode || false,
        flag_tilmidplus: annonce?.flag_tilmidplus || false
    });

    useEffect(() => {
        if (annonce) {
            // Déterminer les flags sélectionnés
            const selectedFlags: ('tilmicode' | 'tilmidplus')[] = [];
            if (annonce.flag_tilmicode) selectedFlags.push('tilmicode');
            if (annonce.flag_tilmidplus) selectedFlags.push('tilmidplus');
            
            form.setFieldsValue({
                ...annonce,
                start_date: annonce.start_date ? dayjs(annonce.start_date) : undefined,
                end_date: annonce.end_date ? dayjs(annonce.end_date) : undefined,
                backgroundColor: annonce.background_color || '#2992BF',
                flags: selectedFlags,
            });
            setPreviewAnnonce(annonce);
        } else {
            form.resetFields();
            setPreviewAnnonce({
                id: 0,
                title: '',
                description: '',
                image_url: '',
                background_color: '#2992BF',
                start_date: '',
                end_date: '',
                actif: false,
                flag_tilmicode: false,
                flag_tilmidplus: false
            });
        }
    }, [annonce, form]);

    const handleFormChange = (_: any, allValues: AnnonceFormValues) => {
        // Déterminer les flags basés sur la sélection
        const flags = allValues.flags || [];
        const flagTilmicode = flags.includes('tilmicode');
        const flagTilmidplus = flags.includes('tilmidplus');
        
        setPreviewAnnonce({
            id: annonce?.id || 0,
            title: allValues.title || '',
            description: allValues.description || '',
            image_url: allValues.image_url || '',
            background_color: allValues.backgroundColor || '#2992BF',
            start_date: allValues.start_date ? allValues.start_date.toISOString() : '',
            end_date: allValues.end_date ? allValues.end_date.toISOString() : '',
            actif: allValues.actif || false,
            flag_tilmicode: flagTilmicode,
            flag_tilmidplus: flagTilmidplus
        });
    };

    const onFinish = (values: AnnonceFormValues) => {
        // Déterminer les flags basés sur la sélection
        const flags = values.flags || [];
        const flagTilmicode = flags.includes('tilmicode');
        const flagTilmidplus = flags.includes('tilmidplus');
        
        // Extraire les propriétés nécessaires sans le champ 'flags' qui n'est pas dans TAnnonce
        const { flags: formFlags, backgroundColor, start_date, end_date, ...restValues } = values;
        
        const payload: TAnnonce = {
            ...restValues,
            start_date: start_date ? start_date.toISOString() : undefined,
            end_date: end_date ? end_date.toISOString() : undefined,
            background_color: backgroundColor || '#2992BF',
            flag_tilmicode: flagTilmicode,
            flag_tilmidplus: flagTilmidplus,
        };

        const action = isEditMode && annonce
            ? updateAnnonce(annonce.id, payload)
            : createAnnonce(payload);

        action
            .then((resp) => {
                setAnnonces((prev) =>
                    isEditMode && annonce
                        ? prev.map((item) => (item.id === annonce.id ? resp.data : item))
                        : [...prev, resp.data]
                );
                message.success(isEditMode ? t('admin.advert.successMessages.updated') : t('admin.advert.successMessages.created'));
                onClose();
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message);
            });
    };

    return (
        <Modal
            title={
                <Title level={3} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: palette.textPrimary }}>
                    {isEditMode ? t('admin.advert.form.modalTitle.edit') : t('admin.advert.form.modalTitle.create')}
                </Title>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            width={1000}
            styles={{
                content: {
                    borderRadius: 28,
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(244,248,251,0.95))',
                    border: `1px solid ${palette.border}`,
                    boxShadow: '0 30px 70px rgba(15,23,42,0.15)',
                },
                header: {
                    background: 'transparent',
                    borderBottom: `1px solid ${palette.border}`,
                    padding: '24px 32px',
                },
                body: {
                    padding: '32px',
                },
            }}
        >
            <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={handleFormChange}>
                <Row gutter={[32, 24]}>
                    <Col xs={24}>
                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.title')}</span>}
                            name="title"
                            rules={[{ required: true, message: t('admin.advert.form.titleRequired') }]}
                        >
                            <Input
                                size="large"
                                placeholder={t('admin.advert.form.titlePlaceholder')}
                                style={{
                                    borderRadius: 14,
                                    border: `1px solid ${palette.border}`,
                                    padding: '12px 16px',
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.description')}</span>}
                            name="description"
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder={t('admin.advert.form.descriptionPlaceholder')}
                                style={{
                                    borderRadius: 14,
                                    border: `1px solid ${palette.border}`,
                                    padding: '12px 16px',
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.imageUrl')}</span>}
                            name="image_url"
                        >
                            <Input
                                size="large"
                                placeholder={t('admin.advert.form.imageUrlPlaceholder')}
                                style={{
                                    borderRadius: 14,
                                    border: `1px solid ${palette.border}`,
                                    padding: '12px 16px',
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.backgroundColor')}</span>}
                            name="backgroundColor"
                        >
                            <ColorPicker
                                format="hex"
                                showText
                                size="large"
                                onChange={(color) => {
                                    const hexColor = color.toHexString();
                                    form.setFieldsValue({ backgroundColor: hexColor });
                                    setPreviewAnnonce((prev) => ({
                                        ...prev,
                                        background_color: hexColor,
                                    }));
                                }}
                                onChangeComplete={(color) => {
                                    const hexColor = color.toHexString();
                                    form.setFieldsValue({ backgroundColor: hexColor });
                                    setPreviewAnnonce((prev) => ({
                                        ...prev,
                                        background_color: hexColor,
                                    }));
                                }}
                                styles={{
                                    popupOverlayInner: {
                                        borderRadius: 14,
                                    },
                                }}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.startDate')}</span>}
                                    name="start_date"
                                    rules={[{ required: true, message: t('admin.advert.form.startDateRequired') }]}
                                >
                                    <DatePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm"
                                        placeholder={t('admin.advert.form.startDatePlaceholder')}
                                        style={{
                                            width: '100%',
                                            borderRadius: 14,
                                            border: `1px solid ${palette.border}`,
                                            padding: '8px 12px',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.endDate')}</span>}
                                    name="end_date"
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const start_date = getFieldValue('start_date');
                                                if (!value || !start_date || value.isAfter(start_date)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(t('admin.advert.form.endDateAfterStart')));
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm"
                                        placeholder={t('admin.advert.form.endDatePlaceholder')}
                                        style={{
                                            width: '100%',
                                            borderRadius: 14,
                                            border: `1px solid ${palette.border}`,
                                            padding: '8px 12px',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.active')}</span>}
                            name="actif"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren={t('admin.advert.status.yes')}
                                unCheckedChildren={t('admin.advert.status.no')}
                                style={{
                                    background: palette.border,
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ fontWeight: 600, color: palette.textPrimary }}>{t('admin.advert.form.platforms')}</span>}
                            name="flags"
                            rules={[
                                { 
                                    required: true, 
                                    message: t('admin.advert.form.platformsRequired')
                                },
                                {
                                    validator: (_, value) => {
                                        if (!value || value.length === 0) {
                                            return Promise.reject(new Error(t('admin.advert.form.platformsRequired')));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Checkbox.Group
                                style={{
                                    display: 'flex',
                                    gap: 16,
                                    width: '100%',
                                }}
                            >
                                <Checkbox
                                    value="tilmicode"
                                    style={{
                                        borderRadius: 14,
                                        padding: '8px 16px',
                                        border: `1px solid ${palette.border}`,
                                        marginRight: 0,
                                    }}
                                >
                                    {t('admin.advert.form.platformTilmicode')}
                                </Checkbox>
                                <Checkbox
                                    value="tilmidplus"
                                    style={{
                                        borderRadius: 14,
                                        padding: '8px 16px',
                                        border: `1px solid ${palette.border}`,
                                        marginRight: 0,
                                    }}
                                >
                                    {t('admin.advert.form.platformTilmidplus')}
                                </Checkbox>
                            </Checkbox.Group>
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ marginTop: 32 }}>
                    <div
                        style={{
                            marginBottom: 16,
                            padding: '16px 20px',
                            borderRadius: 16,
                            background: 'linear-gradient(135deg, rgba(41,146,191,0.1), rgba(139,190,30,0.08))',
                            border: `1px solid ${palette.border}`,
                        }}
                    >
                        <Title 
                            level={4} 
                            style={{ 
                                margin: 0, 
                                fontSize: '1.15rem', 
                                fontWeight: 700, 
                                color: palette.textPrimary, 
                                textAlign: 'center' 
                            }}
                        >
                            {t('admin.advert.form.preview')}
                        </Title>
                    </div>
                    <div
                        style={{
                            padding: '16px',
                            borderRadius: 24,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(244,248,251,0.95))',
                            border: `1px solid ${palette.border}`,
                            boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
                            overflow: 'hidden',
                            maxHeight: 250,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ width: '100%' }}>
                            <AnnonceBanner
                                id={previewAnnonce.id}
                                title={previewAnnonce.title || t('admin.advert.form.previewTitle')}
                                description={previewAnnonce.description || t('admin.advert.form.previewDescription')}
                                image_url={previewAnnonce.image_url}
                                background_color={previewAnnonce.background_color}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 32, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={onClose}
                        size="large"
                        style={{
                            borderRadius: 14,
                            border: `1px solid ${palette.border}`,
                            padding: '8px 24px',
                            height: 'auto',
                        }}
                    >
                        {t('admin.advert.form.buttons.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        style={{
                            borderRadius: 14,
                            background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                            border: 'none',
                            padding: '8px 32px',
                            height: 'auto',
                            fontWeight: 600,
                            boxShadow: '0 8px 20px rgba(41,146,191,0.3)',
                        }}
                    >
                        {isEditMode ? t('admin.advert.form.buttons.save') : t('admin.advert.form.buttons.publish')}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AnnonceBannerForm;
